
import moment from 'moment';

import { FormattedMessage } from 'react-intl';

import { formatDateCheckInOut } from '@share/utils';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { InputFilterType } from '@share/common-types';
import { IColumnMenu } from '@constants';
import { Tooltip } from 'antd';

const MobileFirstColumnBase: IColumnMenu = {
  description: 'Description',
  key: 'description',
  type: InputFilterType.Custom,
  display: true,
  order: 1,
};

const FirstColumnBase: IColumnMenu = {
  description: 'Actions',
  key: 'actions',
  width: 150,
  type: InputFilterType.Custom,
  display: true,
  order: 1,
};

export enum Type {
  Condos,
  Hotels,
  Cars,
  Historicals
};

export type VisibleFunction = (visible: boolean, record?: any) => void;
export type QuoteBaseFunction = (quoteBase: string) => void;

const getActions = (type: Type, isMobile: boolean, record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => {
  const quoteUrl = `${import.meta.env.VITE_API_BFF_URL}/deals/quote/${record.quoteBase}/file`;
  return (
    <>
      <div style={{ marginTop: isMobile ? '15px' : '0px' }}>
        <a
          href="#"
          rel="noreferrer"
          onClick={(e) => {
            e.preventDefault();
            setVisible(true, record);
            setQuoteBase(record.quoteBase);
            markQuotesAsRead(record.quoteBase);
          }}
        >
          <FormattedMessage id={type === Type.Historicals ? 'results.hotel.card.compare.modal.view.only' : 'results.hotel.card.compare.modal.view'} />
        </a>
      </div>
      <div>
        <a
          href={quoteUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            markQuotesAsRead(record.quoteBase);
          }}
        >
          Download Quote
        </a>
      </div>
    </>)
  };

const getMobileBaseColumn = (type: Type, record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, getExtraColumns: (record: any) => JSX.Element, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    <div>
      <strong>Creation Date:</strong>
    </div>
    <div style={{ marginBottom: '6px' }}>
      <span>{moment(record.creationDate, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
    {getExtraColumns(record)}
    <div>
      <strong>City:</strong>
    </div>
    <div style={{ marginBottom: '6px' }}>
      <span>{record.place}</span>
    </div>
    <div>
      <strong>Comment:</strong>
    </div>
    <div>
      <span>{record.comment}</span>
    </div>
    {getActions(type, true, record, setVisible, setQuoteBase, markQuotesAsRead)}
  </>);

const getHotelsMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Hotels, record, setVisible, setQuoteBase, (record: any) => getHotelsMobileExtraColumn(record), markQuotesAsRead)}
  </>);

const getHotelsMobileExtraColumn = (record: any) => (
  <>
    <div>
      <strong>Check In:</strong>
    </div>
    <div style={{ marginBottom: '6px' }}>
      <span>{moment(record.checkIn, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
    <div>
      <strong>Check Out:</strong>
    </div>
    <div style={{ marginBottom: '6px' }}>
      <span>{moment(record.checkOut, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
  </>);

const getAvailableDateText = (availableDates: any[]) => (
  <div style={{ display: 'block' }}>
    {availableDates?.map((date, index) => (
      <label key={index} style={{ marginRight: '6px', whiteSpace: 'nowrap' }}>{index + 1}:<strong style={{ marginLeft: '6px' }}>({date.availableDates?.length ? date.availableDates.length : 'NA'})</strong>,</label>
    ))}
  </div>
);

const getCondosMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Condos, record, setVisible, setQuoteBase, (record: any) => getCondosMobileExtraColumn(record), markQuotesAsRead)}
  </>);

const getCondosMobileExtraColumn = (record: any) => (
  <>
    <div>
      <strong>Available Dates:</strong>
    </div>
    <div style={{ marginBottom: '6px' }}>
      {getAvailableDateText(record.availableDates)}
    </div>
  </>);

const getCarsMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Hotels, record, setVisible, setQuoteBase, (record: any) => getCarsMobileExtraColumn(record), markQuotesAsRead)}
  </>);

const getCarsMobileExtraColumn = (record: any) => (
  <>
    <div>
      <strong>Check In:</strong>
    </div>
    <div style={{ marginBottom: '6px' }}>
      <span>{moment(record.checkIn, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
    <div>
      <strong>Check Out:</strong>
    </div>
    <div style={{ marginBottom: '6px' }}>
      <span>{moment(record.checkOut, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
  </>);


const getHistoricalMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Historicals, record, setVisible, setQuoteBase, (record: any) => {
      const getMobileColumn = record.isCondo ? getCondosMobileExtraColumn : getHotelsMobileExtraColumn;
      return getMobileColumn(record);
    }, markQuotesAsRead)}
  </>);

const ColumnsHotelsBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check In', key: 'checkIn', width: 120, display: true, order: 2, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check Out', key: 'checkOut', width: 120, display: true, order: 3, type: InputFilterType.Text, displayConditions: true },
  { description: 'City', key: 'place', width: 300, display: true, order: 4, type: InputFilterType.Text, displayConditions: false },
  { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
];

const ColumnsCondosBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  {
    description: 'Available Dates',
    key: 'availableDates',
    width: 250,
    display: true,
    order: 2,
    type: InputFilterType.Custom,
    component: (_text: any, record?: any) => {
      const availableDateText = getAvailableDateText(record.availableDates);
      return <Tooltip title={availableDateText}>{availableDateText}</Tooltip>;
    }
  },
  { description: 'City', key: 'place', width: 300, display: true, order: 4, type: InputFilterType.Text, displayConditions: false },
  { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
];

const ColumnsCarsBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check In', key: 'checkIn', width: 120, display: true, order: 2, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check Out', key: 'checkOut', width: 120, display: true, order: 3, type: InputFilterType.Text, displayConditions: true },
  { description: 'PickUp', key: 'pickUpLocationDetail', width: 300, display: true, order: 4, type: InputFilterType.Text, displayConditions: false },
  { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
];

const ColumnsHistoricalBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  {
    description: 'Type',
    key: 'type',
    type: InputFilterType.Custom,
    width: 90,
    display: true,
    order: 2,
    component: (_text: any, record?: any) => (<div> <FormattedMessage id={ record?.quoteType === 'Hotel'? 'Hotel' : record?.quoteType === 'Car'       ? 'Car'   : record?.isCondo? 'Condo': 'Hotel' } /></div>)
  },
  {
    description: 'Dates',
    key: 'dates',
    width: 200,
    display: true,
    order: 4,
    type: InputFilterType.Custom,
    component: (_text: any, record?: any) => {
      const isCondo = record.isCondo;
      if (!isCondo) {
        const dates = `${record.checkIn} - ${record.checkOut}`;
        return <Tooltip title={dates}>{dates}</Tooltip>;
      }
      const availableDateText = getAvailableDateText(record.availableDates);
      return <Tooltip title={availableDateText}>{availableDateText}</Tooltip>;
    }
  },
  { description: 'City', key: 'place', width: 280, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
  { description: 'Comment', key: 'comment', width: 200, display: true, order: 6, type: InputFilterType.Text, displayConditions: false },
];

const GetColumns = (type: Type, isMobile: boolean, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => {
  if (isMobile) {
    // const getMobileColumn = type === Type.Condos ? getCondosMobileColumn : type === Type.Hotels ? getHotelsMobileColumn : getHistoricalMobileColumn;
    let getMobileColumn;
    switch (type) {
      case Type.Condos:
        getMobileColumn = getCondosMobileColumn;
        break;
      case Type.Hotels:
        getMobileColumn = getHotelsMobileColumn;
        break;
      case Type.Cars:
        getMobileColumn = getCarsMobileColumn;
        break;
      default:
        getMobileColumn = getHistoricalMobileColumn;
    }
    return [{
      ...MobileFirstColumnBase,
      component: (_text: string, record: any) => getMobileColumn(record, setVisible, setQuoteBase, markQuotesAsRead)
    }];
  } else {
    const columnsBase =type === Type.Condos? ColumnsCondosBase: type === Type.Hotels? ColumnsHotelsBase: type === Type.Cars? ColumnsCarsBase: ColumnsHistoricalBase;
    return [{
        ...FirstColumnBase,
        component: (_text: string, record: any) => {return getActions(type, false, record, setVisible, setQuoteBase, markQuotesAsRead)}
      },
      ...columnsBase
    ];
  }
}

export const GetQuoteDatasourceData = (quote: any, originalList?: any[]) => {
  const isCondo = quote.isCondo;
  const data = {
    quoteBase: quote.quoteBaseId,
    creationDate: formatDateCheckInOut(quote.creationDate, DATE_FORMAT_CHECK_IN_OUT),
    place: quote.place,
    comment: quote.comment,
    fileUrl: quote.fileUrl,
    isCondo,
    quoteType:quote.quoteType,
    pickUpLocationDetail:quote.pickUpLocationDetail
  } as any;

  if (isCondo) {
    data.availableDates = originalList?.filter(q => q.quoteBaseId === quote.quoteBaseId).map(({ availableDates }) => ({ availableDates }));
  } else {
    data.checkIn = formatDateCheckInOut(quote.checkIn, DATE_FORMAT_CHECK_IN_OUT);
    data.checkOut = formatDateCheckInOut(quote.checkOut, DATE_FORMAT_CHECK_IN_OUT);
  }

  return data;
}

export const HOTELS_KEY = '1';
export const CONDOS_KEY = '2';
export const CARS_KEY = '3';
export const HISTORICAL_KEY = '4';

export const GetMyQuotesTabs = (isMobile: boolean, dataSourceHotels: any[], dataSourceCondos: any[],dataSourceCars: any[], dataSourceHistorical: any[], setVisibleHotels: VisibleFunction, setVisibleCondos: VisibleFunction,setVisibleCars: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => {
  return [
    { 
      key: HOTELS_KEY,
      title: 'hotels',
      columns: GetColumns(Type.Hotels, isMobile, setVisibleHotels, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceHotels
    },
    { 
      key: CONDOS_KEY,
      title: 'condos',
      columns: GetColumns(Type.Condos, isMobile, setVisibleCondos, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceCondos
    },
    { 
      key: CARS_KEY,
      title: 'cars',
      columns: GetColumns(Type.Cars, isMobile, setVisibleCars, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceCars
    },
    {
      key: HISTORICAL_KEY,
      title: 'historical',
      columns: GetColumns(Type.Historicals, isMobile, (visible: boolean, record?: any) => {
        if (record.isCondo) {
          setVisibleCondos(visible);
        } else if (record.quoteType=='Hotel')  {
          setVisibleHotels(visible);
        } else if (record.quoteType=='Car')  {
          setVisibleCars(visible);
        }
      }, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceHistorical
    },
  ];

}
